import React, { Component, ReactNode } from "react"

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  isChunkError: boolean
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    error.message.includes("Loading chunk") ||
    error.message.includes("Failed to fetch dynamically imported module")
  )
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, isChunkError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error)
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by Error Boundary:", error, errorInfo)
  }

  handleRetry = () => {
    if (this.state.isChunkError) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, isChunkError: false })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-sm text-gray-600">
            {this.state.isChunkError
              ? "A new version may be available, or there was a network issue."
              : "Something went wrong loading this page."}
          </p>
          <button
            onClick={this.handleRetry}
            className="rounded-md bg-primary-400 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
          >
            {this.state.isChunkError ? "Reload" : "Try again"}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
